import {PortableText} from '@portabletext/react';
import {useEffect, useMemo, useState} from 'react';
import {AnnouncementBarEntity} from '~/types';
import {IconArrow} from '../icons/IconArrow';
import {DATA_TEST_IDS} from '~/lib/const';

let interval: NodeJS.Timer;

export function AnnouncementBar(props: {
  announcementBar: AnnouncementBarEntity[] | undefined;
}) {
  const [currentAnnouncementBar, setCurrentAnnouncementBar] = useState(0);

  const announcementBar = useMemo(
    () =>
      props.announcementBar?.filter((a) => a.enabled && a.textContent) ?? [],
    [props.announcementBar],
  );

  useEffect(() => {
    if (announcementBar.length > 1) {
      interval = setInterval(() => {
        setCurrentAnnouncementBar((prev) =>
          prev === announcementBar.length - 1 ? 0 : prev + 1,
        );
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [announcementBar, currentAnnouncementBar]);

  if (announcementBar.length === 0) {
    return null;
  }

  return (
    <div
      data-testid={DATA_TEST_IDS.ANNOUNCEMENT_BAR}
      className="h-[48px] bg-blue-400 flex items-center justify-center relative z-10"
    >
      <div className="w-full h-full items-center flex px-5 xl:px-[97px] max-w-[1728px] mx-auto">
        {announcementBar.length > 1 && (
          <button
            data-testid={DATA_TEST_IDS.BUTTON('prev-announcement')}
            aria-label="Previous Announcement Bar"
            type="button"
            className="h-full px-1"
            onClick={() => {
              setCurrentAnnouncementBar((prev) =>
                prev === 0 ? announcementBar.length - 1 : prev - 1,
              );
              clearInterval(interval);
            }}
          >
            <IconArrow className="rotate-180 text-white scale-75" />
          </button>
        )}

        <div className="relative w-full h-full flex items-center overflow-hidden">
          {announcementBar.map((announcementBar, index) => {
            return (
              <div
                data-testid={DATA_TEST_IDS.ANNOUNCEMENT_CONTENT}
                key={announcementBar._id}
                className="text-18/18 font-header font-bold text-white text-center tracking-wide w-full transform -translate-x-1/2 absolute transition-all line-clamp-2 announcement-bar-text"
                style={{
                  left: `calc(50% + ${
                    (index - currentAnnouncementBar) * 28
                  }rem)`,
                  opacity: index === currentAnnouncementBar ? 1 : 0,
                  zIndex: index === currentAnnouncementBar ? 1 : 0,
                }}
              >
                <PortableText value={announcementBar.textContent!} />
              </div>
            );
          })}
        </div>

        {announcementBar.length > 1 && (
          <button
            data-testid={DATA_TEST_IDS.BUTTON('next-announcement')}
            aria-label="Next Announcement Bar"
            type="button"
            className="h-full px-1"
            onClick={() => {
              setCurrentAnnouncementBar((prev) =>
                prev === announcementBar.length - 1 ? 0 : prev + 1,
              );
              clearInterval(interval);
            }}
          >
            <IconArrow className="text-white scale-75" />
          </button>
        )}
      </div>
    </div>
  );
}
